'use client';

import React, { useState } from 'react'; // Menambahkan useState
import { motion, AnimatePresence } from 'framer-motion'; // Menambahkan AnimatePresence
import Link from 'next/link';
import Image from 'next/image';

const stats = [
  { value: '14', label: 'Tahun Berpengalaman', icon: '🏆' },
  { value: '3.000+', label: 'Proyek Selesai', icon: '⚡' },
  { value: '1.000+', label: 'Klien Puas', icon: '❤️' },
  { value: '30+', label: 'Kota Dilayani', icon: '📍' }
];

const team = [
  {
    name: 'Bayu Akbar',
    role: 'Founder & Director',
    initials: 'BA',
  },
  {
    name: 'Achmad Basri',
    role: 'Supervisor',
    initials: 'AB',
  },
  {
    name: 'Gilang',
    role: 'Sales Marketing',
    initials: 'G',
  },
];

const equipment = [
  { name: 'Fiber Laser Cutter', spec: '1.5kW – 6kW', desc: 'Pemotongan metal presisi tinggi' },
  { name: 'CO2 Laser Cutter', spec: '100W – 300W', desc: 'Acrylic, kayu, kain, kulit' },
  { name: 'CNC Router 4x8ft', spec: 'Spindle 3kW', desc: 'Kayu solid, MDF, aluminium' },
  { name: 'Plasma Cutter CNC', spec: 'Ketebalan 0–25mm', desc: 'Plat besi & stainless tebal' }
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

export default function AboutContent() {
  // Fitur Baru: State untuk menyimpan gambar yang diklik
  const [selectedImg, setSelectedImg] = useState<string | null>(null);

  return (
    <>
      {/* Fitur Baru: Pop-up Lightbox yang bisa digeser */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/90 p-4 md:p-10 cursor-pointer"
            onClick={() => setSelectedImg(null)}
          >
            {/* Tombol Close */}
            <button className="absolute top-8 right-8 text-white text-4xl hover:scale-110 transition-transform">
              &times;
            </button>

            <motion.div
              drag // Mengaktifkan fitur geser
              dragConstraints={{ left: -100, right: 100, top: -100, bottom: 100 }} // Batas geser
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative w-full max-w-5xl h-[70vh] cursor-grab active:cursor-grabbing"
              onClick={(e) => e.stopPropagation()} // Supaya tidak tertutup saat klik gambar
            >
              <Image
                src={selectedImg}
                alt="Enlarged view"
                fill
                className="object-contain pointer-events-none"
                priority
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <section className="relative py-20 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <span className="text-xs uppercase tracking-widest text-accent font-semibold mb-4 block">
                Tentang Kami
              </span>
              <h1 className="text-display font-bold text-foreground tracking-tight mb-6">
                Indo Cutting
                <br />
                <span className="text-accent">Laser</span>
              </h1>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Indo Cutting Laser adalah penyedia jasa manufaktur presisi yang berfokus pada solusi Laser Cutting Plat dan CNC Router. Kami hadir sebagai mitra terpercaya bagi para arsitek, kontraktor, desainer interior, hingga pelaku industri kreatif untuk mewujudkan berbagai desain material menjadi produk jadi yang berkualitas tinggi.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Dengan dukungan teknologi mesin terbaru dan tim teknis yang berpengalaman, kami berkomitmen memberikan hasil potongan yang rapi, akurat, dan sesuai dengan spesifikasi teknis yang Anda butuhkan.
              </p>
              <div className="flex gap-4">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent text-accent-foreground font-semibold text-sm hover:opacity-90 transition-all duration-300 hover:-translate-y-1 shadow-amber-glow"
                >
                  Konsultasi Sekarang
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </motion.div>

            <motion.div 
              variants={itemVariants} 
              className="relative cursor-zoom-in"
              onClick={() => setSelectedImg('/assets/images/lcf.jpeg')} // Trigger Pop-up
            >
              <div className="relative rounded-2xl overflow-hidden shadow-strong border border-border image-zoom-container h-80 md:h-96 hover:opacity-90 transition-opacity">
              <Image
                src="/assets/images/lcf.jpeg"
                alt="IndoCuttingLaser workshop interior"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw" 
              />
            </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
        
      {/* Stats */}
      <section className="py-12 px-4 md:px-8 bg-secondary/50 dark:bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                className="bg-card rounded-2xl p-6 text-center shadow-soft border border-border card-hover"
              >
                <span className="text-3xl mb-2 block">{stat.icon}</span>
                <p className="text-2xl md:text-3xl font-bold text-accent mb-1">{stat.value}</p>
                <p className="text-muted-foreground text-xs uppercase tracking-wider">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Equipment */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="mb-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-xs uppercase tracking-widest text-accent font-semibold mb-3 block">
              Armada Mesin
            </span>
            <h2 className="text-display font-bold text-foreground tracking-tight">
              Teknologi Terkini
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {equipment.map((eq, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                className="bg-card rounded-2xl p-6 border border-border shadow-soft card-hover group"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary dark:text-primary mb-4 group-hover:scale-110 transition-transform duration-300">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-5 h-5">
                    <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h3 className="font-bold text-foreground mb-1">{eq.name}</h3>
                <span className="inline-block px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-2">
                  {eq.spec}
                </span>
                <p className="text-muted-foreground text-sm">{eq.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-secondary/50 dark:bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-xs uppercase tracking-widest text-accent font-semibold mb-3 block">
              Tim Kami
            </span>
            <h2 className="text-display font-bold text-foreground tracking-tight">
              Ahli di Balik Presisi
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {team.map((member, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                className="bg-card rounded-2xl overflow-hidden shadow-soft border border-border card-hover group"
              >
                <div className="h-56 flex items-center justify-center bg-gradient-to-br from-primary to-accent">
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold"
                    style={{
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,255,255,0.2)',
                    }}
                  >
                    {member.initials}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-foreground text-lg">{member.name}</h3>
                  <p className="text-accent text-sm font-medium">{member.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <span className="text-xs uppercase tracking-widest text-accent font-semibold mb-3 block">
                Nilai Kami
              </span>
              <h2 className="text-display font-bold text-foreground tracking-tight mb-6">
                Mengapa Ribuan Klien
                <br />
                <span className="text-accent">Mempercayai Kami?</span>
              </h2>
              <div className="flex flex-col gap-4">
                {[
                  { title: 'Presisi Tinggi', desc: 'Menggunakan teknologi sensor dan kontrol numerik komputer (CNC) yang menjamin tingkat akurasi hingga detail terkecil.' },
                  { title: 'Konsultasi Desain', desc: 'Belum punya pola? Tim kami siap membantu mengarahkan desain yang paling efektif dan estetis untuk kebutuhan proyek Anda.' },
                  { title: 'Efisiensi Material', desc: 'Proses penataan pola (nesting) yang optimal memastikan penggunaan bahan yang lebih hemat dan efisien bagi pelanggan.' },
                  { title: 'Pengerjaan Cepat', desc: 'Kami memahami pentingnya tenggat waktu proyek Anda. Dengan alur kerja yang sistematis, kami menjamin durasi produksi yang kompetitif.' },
                  { title: 'Harga Transparan', desc: 'Penawaran jelas tanpa biaya tersembunyi' }
                ].map((v, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border hover:border-accent transition-colors duration-300">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center text-accent flex-shrink-0">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-0.5">{v.title}</h4>
                      <p className="text-muted-foreground text-sm">{v.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              variants={itemVariants} 
              className="relative cursor-zoom-in"
              onClick={() => setSelectedImg('/assets/cncrouter/Acp.png')} // Trigger Pop-up
            >
              <div className="relative rounded-2xl overflow-hidden shadow-strong border border-border image-zoom-container h-80 md:h-96 hover:opacity-90 transition-opacity">
                <Image
                  src="/assets/cncrouter/Acp.png"
                  alt="IndoCuttingLaser workshop interior"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw" 
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 md:px-8 bg-secondary/50 dark:bg-secondary/30">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-display font-bold text-foreground mb-4">
              Siap Mulai Proyek Anda?
            </h2>
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Hubungi tim kami sekarang untuk konsultasi gratis. Kami akan membantu Anda menemukan solusi terbaik.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-accent text-accent-foreground font-bold text-sm hover:opacity-90 transition-all duration-300 hover:-translate-y-1 shadow-amber-glow"
              >
                Hubungi Kami
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a
                href="https://wa.me/628132282297"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-green-500 hover:bg-green-400 text-white font-bold text-sm transition-all duration-300 hover:-translate-y-1 shadow-green-glow"
              >
                WhatsApp Langsung
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}